import React from 'react';
import type { VocabularyGroup } from '../../types/vocabulary.types';

interface GroupFilterBarProps {
  groups: VocabularyGroup[];
  selectedGroupId: string;
  totalCount: number;
  onSelectGroup: (groupId: string) => void;
  onOpenCreateModal: () => void;
  onOpenRenameModal: (group: VocabularyGroup) => void;
  onOpenDeleteModal: (group: VocabularyGroup) => void;
  isLoading?: boolean;
}

export const GroupFilterBar: React.FC<GroupFilterBarProps> = ({
  groups,
  selectedGroupId,
  totalCount,
  onSelectGroup,
  onOpenCreateModal,
  onOpenRenameModal,
  onOpenDeleteModal,
  isLoading = false,
}) => {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <span>Collections & Groups</span>
        </span>

        {/* New Group Button */}
        <button
          id="create-group-btn"
          type="button"
          onClick={onOpenCreateModal}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-primary-300 bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/30 hover:border-primary-500/50 transition-all transform active:scale-95"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          <span>New Group</span>
        </button>
      </div>

      {/* Horizontal Scrollable Chips Row */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1.5 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        {/* All Words Chip */}
        <button
          id="filter-group-all-btn"
          type="button"
          onClick={() => onSelectGroup('all')}
          className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
            selectedGroupId === 'all'
              ? 'bg-gradient-to-r from-primary-600 to-accent-600 text-white shadow-md shadow-primary-900/30 ring-1 ring-primary-400'
              : 'bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10'
          }`}
        >
          <span>All Vocabulary</span>
          <span
            className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
              selectedGroupId === 'all'
                ? 'bg-white/20 text-white'
                : 'bg-white/10 text-slate-400'
            }`}
          >
            {totalCount}
          </span>
        </button>

        {/* Ungrouped Chip */}
        <button
          id="filter-group-ungrouped-btn"
          type="button"
          onClick={() => onSelectGroup('ungrouped')}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
            selectedGroupId === 'ungrouped'
              ? 'bg-gradient-to-r from-slate-700 to-surface-700 text-white shadow-md ring-1 ring-slate-400'
              : 'bg-white/5 hover:bg-white/10 text-slate-400 border border-white/10'
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <span>Ungrouped</span>
        </button>

        {/* Custom Group Chips */}
        {groups.map((group) => {
          const isSelected = selectedGroupId === group.id;

          return (
            <div
              key={group.id}
              className={`shrink-0 inline-flex items-center rounded-xl transition-all ${
                isSelected
                  ? 'bg-gradient-to-r from-primary-600/90 to-primary-700/90 text-white shadow-md shadow-primary-950/40 ring-1 ring-primary-400'
                  : 'bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10'
              }`}
            >
              {/* Main selection button */}
              <button
                id={`filter-group-${group.id}-btn`}
                type="button"
                onClick={() => onSelectGroup(group.id)}
                className="flex items-center gap-2 pl-3.5 pr-2 py-1.5 text-xs font-semibold"
              >
                <span className="max-w-[140px] truncate">{group.name}</span>
                {typeof group.wordCount === 'number' && (
                  <span
                    className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-white/10 text-slate-400'
                    }`}
                  >
                    {group.wordCount}
                  </span>
                )}
              </button>

              {/* Group Action Buttons (Rename & Delete) */}
              <div className="flex items-center pr-1.5 border-l border-white/10 ml-0.5">
                <button
                  id={`rename-group-btn-${group.id}`}
                  type="button"
                  title={`Rename "${group.name}"`}
                  aria-label={`Rename ${group.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenRenameModal(group);
                  }}
                  className={`p-1 rounded-md transition-colors ${
                    isSelected
                      ? 'text-white/80 hover:text-white hover:bg-white/10'
                      : 'text-slate-400 hover:text-primary-300 hover:bg-white/10'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                  </svg>
                </button>

                <button
                  id={`delete-group-btn-${group.id}`}
                  type="button"
                  title={`Delete group "${group.name}"`}
                  aria-label={`Delete ${group.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenDeleteModal(group);
                  }}
                  className={`p-1 rounded-md transition-colors ${
                    isSelected
                      ? 'text-rose-200 hover:text-rose-100 hover:bg-rose-500/20'
                      : 'text-slate-400 hover:text-rose-400 hover:bg-rose-500/10'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
